import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MdSearch, MdFitnessCenter } from 'react-icons/md';
import { requestWithApiFallback } from '../../utils/apiRequest';
import styles from './Search.module.css';
import VerifiedBadge from '../../components/VerifiedBadge/VerifiedBadge';
import formatDisplayName from '../../utils/formatDisplayName';

const FILTERS = [
	{ key: 'all', label: 'All' },
	{ key: 'people', label: 'People' },
	{ key: 'routines', label: 'Routines' },
	{ key: 'posts', label: 'Posts' },
	{ key: 'meals', label: 'Meals' },
];

const Search = () => {
	const API_URL = process.env.REACT_APP_API_URL;
	const requestApi = useCallback((factory) => requestWithApiFallback(API_URL, factory), [API_URL]);

	const [query, setQuery] = useState('');
	const [filter, setFilter] = useState('all');
	const [results, setResults] = useState(null);
	const [searching, setSearching] = useState(false);
	const [hasSearched, setHasSearched] = useState(false);

	const runSearch = useCallback(async (searchQuery, searchFilter) => {
		const trimmed = (searchQuery || '').trim();
		if (!trimmed) return;
		setSearching(true);
		setHasSearched(true);
		try {
			const response = await requestApi((base) =>
				axios.get(`${base}/search/unified`, { params: { q: trimmed, filter: searchFilter, limit: 20 } })
			);
			setResults(response.data);
		} catch (error) {
			console.error('Search error:', error.message);
			setResults(null);
		} finally {
			setSearching(false);
		}
	}, [requestApi]);

	const handleSubmit = (e) => {
		e?.preventDefault?.();
		runSearch(query, filter);
	};

	const handleFilterChange = (newFilter) => {
		setFilter(newFilter);
		if (query.trim()) {
			runSearch(query, newFilter);
		}
	};

	const formatDate = (val) => {
		try { return new Date(val).toLocaleDateString(); } catch { return ''; }
	};

	const hasPeople = results?.people?.length > 0;
	const hasRoutines = results?.routines?.length > 0;
	const hasPosts = results?.posts?.length > 0;
	const hasMeals = results?.meals?.length > 0;
	const hasResults = hasPeople || hasRoutines || hasPosts || hasMeals;

	return (
		<div className={styles.SearchPage}>
			<div className={styles.SearchHeader}>
				<h1 className={styles.SearchTitle}>Search</h1>
				<form className={styles.SearchBarRow} onSubmit={handleSubmit}>
					<input
						className={styles.SearchInput}
						type='text'
						placeholder='Search people, routines, posts, meals...'
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						autoFocus
					/>
					<button
						type='submit'
						className={styles.SearchBtn}
						disabled={!query.trim() || searching}
					>
						<MdSearch />
					</button>
				</form>
				<div className={styles.FilterRow}>
					{FILTERS.map((f) => (
						<button
							type='button'
							key={f.key}
							className={`${styles.FilterChip} ${filter === f.key ? styles.ActiveChip : ''}`}
							onClick={() => handleFilterChange(f.key)}
						>
							{f.label}
						</button>
					))}
				</div>
			</div>

			<div className={styles.ResultsArea}>
				{searching && <p className={styles.MutedText}>Searching...</p>}

				{!searching && hasSearched && !hasResults && (
					<div className={styles.EmptyState}>
						<h3>No results</h3>
						<p>Try a different search term or filter.</p>
					</div>
				)}

				{!searching && hasPeople && (
					<div className={styles.ResultGroup}>
						<h4 className={styles.SectionLabel}>People</h4>
						{results.people.map((person) => (
							<Link
								key={person.userID}
								to={`/profile/${person.userID}`}
								className={styles.PersonRow}
							>
								{person.profileImageLink ? (
									<img src={person.profileImageLink} alt='' className={styles.PersonAvatar} />
								) : (
									<div className={styles.PersonAvatar} />
								)}
								<div className={styles.PersonInfo}>
									<span className={styles.PersonName}>
										{formatDisplayName(person.username)}
										<VerifiedBadge userPlan={person.userPlan} totalXP={person.totalXP} size='small' />
									</span>
									{person.bio ? (
										<span className={styles.PersonSub}>{person.bio}</span>
									) : (
										<span className={styles.PersonSub}>{person.totalXP || 0} XP</span>
									)}
								</div>
							</Link>
						))}
					</div>
				)}

				{!searching && hasRoutines && (
					<div className={styles.ResultGroup}>
						<h4 className={styles.SectionLabel}>Routines</h4>
						{results.routines.map((r, i) => (
							<Link
								key={`${r.creatorUserID}-${r.routineName}-${i}`}
								to={`/stryd/${r.creatorUserID}/${r.routineName}`}
								className={styles.RoutineRow}
							>
								<MdFitnessCenter />
								<div className={styles.RoutineInfo}>
									<span className={styles.RoutineName}>{r.routineName}</span>
									<div className={styles.RoutineMeta}>
										<span>
											by {formatDisplayName(r.creatorUsername)}
											<VerifiedBadge userPlan={r.creatorUserPlan} totalXP={r.creatorTotalXP} size='small' />
										</span>
										{r.estimatedTimeMinutes && <span>{r.estimatedTimeMinutes} min</span>}
										<span>{r.workoutCount} exercises</span>
									</div>
								</div>
							</Link>
						))}
					</div>
				)}

				{!searching && hasPosts && (
					<div className={styles.ResultGroup}>
						<h4 className={styles.SectionLabel}>Posts</h4>
						{results.posts.map((post) => (
							<Link
								key={post._id}
								to={`/feed/post/${post._id}`}
								className={styles.PostRow}
							>
								<div className={styles.PostRowHeader}>
									<span className={styles.PostRowUsername}>
										{formatDisplayName(post.username)}
										<VerifiedBadge userPlan={post.userPlan} totalXP={post.totalXP} size='small' />
									</span>
									<span className={styles.PostRowTime}>{formatDate(post.createdAt)}</span>
								</div>
								<p className={styles.PostRowContent}>{post.content}</p>
								<div className={styles.PostRowMeta}>
									<span>{post.likeCount} likes</span>
									<span>{post.commentCount} replies</span>
								</div>
							</Link>
						))}
					</div>
				)}

				{!searching && hasMeals && (
					<div className={styles.ResultGroup}>
						<h4 className={styles.SectionLabel}>Meals</h4>
						{results.meals.map((meal, i) => (
							<div key={`${meal.creatorUserID}-${meal.planName}-${i}`} className={styles.MealRow}>
								<span className={styles.MealName}>{meal.planName}</span>
								<div className={styles.MealMeta}>
									<span>
										by {formatDisplayName(meal.creatorUsername)}
										<VerifiedBadge userPlan={meal.creatorUserPlan} totalXP={meal.creatorTotalXP} size='small' />
									</span>
									<span>{meal.dayCount} days</span>
									{meal.reason && <span>{meal.reason}</span>}
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default Search;
